import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Mail, ArrowLeft, Loader2, KeyRound, Lock, CheckCircle2, 
  RefreshCw, Eye, EyeOff, ShieldCheck, Check, AlertCircle 
} from "lucide-react";
import { motion } from "framer-motion";
import { getApiUrl } from "../apiConfig";
import ToastPopup from "../components/ToastPopup";

const ForgotPasswordPage = () => {
  // Step 1 = Request OTP, Step 2 = Enter OTP & New Password, Step 3 = Success
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // Field-level error tracking
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Global Toast Popup State
  const [toast, setToast] = useState({
    isOpen: false,
    type: "error",
    title: "",
    message: "",
  });

  const showToast = (type, title, message) => {
    setToast({
      isOpen: true,
      type,
      title,
      message,
    });
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, isOpen: false }));
  };

  // Basic email regex
  const isValidEmail = (val) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  };

  // Step 1: Request OTP
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    const cleanEmail = email.trim();
    if (!cleanEmail) {
      setFieldErrors({ email: "Email address is required" });
      showToast("error", "Missing Email", "Please enter your registered email address.");
      return;
    }

    if (!isValidEmail(cleanEmail)) {
      setFieldErrors({ email: "Please enter a valid email format" });
      showToast("warning", "Invalid Email Format", "Make sure your email looks like name@example.com");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(getApiUrl("/api/auth/forgot-password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: cleanEmail }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showToast("success", "Code Sent!", `A 6-digit OTP code has been sent to ${cleanEmail}`);
        setStep(2);
      } else {
        const errorText = data.message || "Failed to send verification code.";
        setFieldErrors({ email: errorText });
        showToast("error", "Account Not Found", errorText);
      }
    } catch (err) {
      console.error("Forgot password request error:", err);
      showToast("error", "Network Error", "Unable to connect to server. Please check your internet connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Resend OTP in Step 2
  const handleResendOtp = async () => {
    if (isResending) return;
    setIsResending(true);

    try {
      const response = await fetch(getApiUrl("/api/auth/forgot-password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showToast("success", "Code Resent!", `A fresh 6-digit code was sent to ${email}`);
      } else {
        showToast("error", "Resend Failed", data.message || "Could not resend verification code.");
      }
    } catch (err) {
      showToast("error", "Network Error", "Failed to resend code. Please try again later.");
    } finally {
      setIsResending(false);
    }
  };

  // Step 2: Reset Password with OTP
  const handleResetPassword = async (e) => {
    e.preventDefault();
    const errors = {};

    const cleanOtp = otp.trim();
    if (!cleanOtp) {
      errors.otp = "Verification code is required";
    } else if (cleanOtp.length !== 6) {
      errors.otp = "Verification code must be exactly 6 digits";
    }

    if (!newPassword) {
      errors.newPassword = "New password is required";
    } else if (newPassword.length < 6) {
      errors.newPassword = "Password must be at least 6 characters long";
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Confirm password is required";
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstError = Object.values(errors)[0];
      showToast("error", "Validation Error", firstError);
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);

    try {
      const response = await fetch(getApiUrl("/api/auth/reset-password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          otp: cleanOtp,
          newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showToast("success", "Password Reset!", "Your password has been changed successfully.");
        setStep(3);
      } else {
        const errorText = data.message || "Failed to reset password.";
        setFieldErrors({ otp: errorText });
        showToast("error", "Reset Failed", errorText);
      }
    } catch (err) {
      console.error("Reset password error:", err);
      showToast("error", "Network Error", "Error resetting password. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Live password matching check
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const passwordsMismatch = confirmPassword && newPassword !== confirmPassword;

  return (
    <div className="min-h-[85vh] bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Toast Popup Notification */}
      <ToastPopup
        isOpen={toast.isOpen}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={closeToast}
      />

      {/* Decorative ambient gradients */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="flex justify-center mb-4">
          <motion.div 
            key={step}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className={`h-16 w-16 rounded-2xl flex items-center justify-center shadow-lg transition-all ${
              step === 3 
                ? "bg-emerald-500 text-white shadow-emerald-500/25" 
                : "bg-brand-600 text-white shadow-brand-600/25"
            }`}
          >
            {step === 3 ? (
              <CheckCircle2 size={32} />
            ) : step === 2 ? (
              <ShieldCheck size={32} />
            ) : (
              <KeyRound size={30} />
            )}
          </motion.div>
        </div>

        <h2 className="text-center text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
          {step === 1 && "Forgot Password?"}
          {step === 2 && "Enter Verification Code"}
          {step === 3 && "Password Reset Successful!"}
        </h2>
        <p className="mt-2 text-center text-sm text-slate-600 px-4 max-w-sm mx-auto">
          {step === 1 && "Enter your registered email address to receive a secure 6-digit OTP code."}
          {step === 2 && (
            <span>
              We sent a 6-digit verification code to{" "}
              <strong className="text-slate-800">{email}</strong>
            </span>
          )}
          {step === 3 && "Aapka account password update ho chuka hai. Ab aap apne naye password ke sath login kar sakte hain."}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="bg-white/80 backdrop-blur-xl py-8 px-5 shadow-[0_20px_50px_rgba(0,0,0,0.06)] sm:rounded-3xl sm:px-10 border border-white">
          
          {/* STEP 1: Enter Email */}
          {step === 1 && (
            <motion.form 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-5" 
              onSubmit={handleRequestOtp}
            >
              <div className="space-y-1.5 group">
                <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                  Registered Email Address
                </label>
                <div className="relative">
                  <div className={`absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none transition-colors ${
                    fieldErrors.email ? "text-red-500" : "text-slate-400 group-focus-within:text-brand-600"
                  }`}>
                    <Mail size={18} />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (fieldErrors.email) setFieldErrors({ ...fieldErrors, email: null });
                    }}
                    className={`block w-full pl-10 pr-3.5 py-3 bg-slate-50/70 border rounded-xl text-sm placeholder-slate-400 focus:outline-none focus:ring-4 transition-all ${
                      fieldErrors.email
                        ? "border-red-400 text-red-900 bg-red-50/30 focus:ring-red-500/20 focus:border-red-500"
                        : "border-slate-200 focus:ring-brand-600/15 focus:border-brand-600 hover:border-slate-300"
                    }`}
                    placeholder="name@example.com"
                  />
                </div>
                {fieldErrors.email && (
                  <motion.p 
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-xs font-semibold text-red-600 flex items-center gap-1 mt-1 pl-1"
                  >
                    <AlertCircle size={13} className="shrink-0" />
                    {fieldErrors.email}
                  </motion.p>
                )}
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting || !email.trim()}
                  className={`w-full flex justify-center items-center gap-2 py-3.5 px-4 rounded-xl shadow-sm text-sm font-bold text-white transition-all bg-brand-600 hover:bg-brand-700 active:scale-[0.98] ${
                    (isSubmitting || !email.trim()) 
                      ? "opacity-60 cursor-not-allowed" 
                      : "hover:shadow-lg hover:shadow-brand-600/25"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Sending Verification Code...
                    </>
                  ) : (
                    "Send Verification Code"
                  )}
                </button>
              </div>

              <div className="text-center pt-2">
                <Link to="/my-account" className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-slate-500 hover:text-brand-600 transition-colors">
                  <ArrowLeft size={16} />
                  Back to log in
                </Link>
              </div>
            </motion.form>
          )}

          {/* STEP 2: Enter OTP & New Password */}
          {step === 2 && (
            <motion.form 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4" 
              onSubmit={handleResetPassword}
            >
              {/* OTP Field */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="otp" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                    6-Digit Verification Code
                  </label>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={isResending}
                    className="text-xs font-bold text-brand-600 hover:text-brand-700 flex items-center gap-1 transition-colors"
                  >
                    <RefreshCw size={12} className={isResending ? "animate-spin" : ""} />
                    {isResending ? "Resending..." : "Resend Code"}
                  </button>
                </div>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/[^0-9]/g, ''));
                    if (fieldErrors.otp) setFieldErrors({ ...fieldErrors, otp: null });
                  }}
                  className={`block w-full py-3 px-3 text-center tracking-[10px] font-mono text-2xl font-black bg-slate-50/80 border rounded-xl focus:outline-none focus:ring-4 transition-all ${
                    fieldErrors.otp
                      ? "border-red-400 text-red-900 bg-red-50/30 focus:ring-red-500/20 focus:border-red-500"
                      : "border-slate-200 text-slate-900 focus:ring-brand-600/15 focus:border-brand-600 hover:border-slate-300"
                  }`}
                  placeholder="------"
                  autoFocus
                />
                {fieldErrors.otp && (
                  <p className="text-xs font-semibold text-red-600 flex items-center gap-1 mt-1 pl-1">
                    <AlertCircle size={13} className="shrink-0" />
                    {fieldErrors.otp}
                  </p>
                )}
              </div>

              {/* New Password */}
              <div className="space-y-1.5">
                <label htmlFor="newPassword" block text-xs font-bold uppercase tracking-wider text-slate-700>
                  New Password
                </label>
                <div className="relative">
                  <div className={`absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none ${
                    fieldErrors.newPassword ? "text-red-500" : "text-slate-400"
                  }`}>
                    <Lock size={17} />
                  </div>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (fieldErrors.newPassword) setFieldErrors({ ...fieldErrors, newPassword: null });
                    }}
                    className={`block w-full pl-10 pr-10 py-2.5 bg-slate-50/70 border rounded-xl text-sm placeholder-slate-400 focus:outline-none focus:ring-4 transition-all ${
                      fieldErrors.newPassword
                        ? "border-red-400 text-red-900 bg-red-50/30 focus:ring-red-500/20 focus:border-red-500"
                        : "border-slate-200 focus:ring-brand-600/15 focus:border-brand-600 hover:border-slate-300"
                    }`}
                    placeholder="Min 6 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {fieldErrors.newPassword && (
                  <p className="text-xs font-semibold text-red-600 flex items-center gap-1 mt-1 pl-1">
                    <AlertCircle size={13} className="shrink-0" />
                    {fieldErrors.newPassword}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-1.5">
                <label htmlFor="confirmPassword" block text-xs font-bold uppercase tracking-wider text-slate-700>
                  Confirm New Password
                </label>
                <div className="relative">
                  <div className={`absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none ${
                    fieldErrors.confirmPassword || passwordsMismatch ? "text-red-500" : "text-slate-400"
                  }`}>
                    <Lock size={17} />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (fieldErrors.confirmPassword) setFieldErrors({ ...fieldErrors, confirmPassword: null });
                    }}
                    className={`block w-full pl-10 pr-10 py-2.5 bg-slate-50/70 border rounded-xl text-sm placeholder-slate-400 focus:outline-none focus:ring-4 transition-all ${
                      fieldErrors.confirmPassword || passwordsMismatch
                        ? "border-red-400 text-red-900 bg-red-50/30 focus:ring-red-500/20 focus:border-red-500"
                        : passwordsMatch
                        ? "border-emerald-500 text-emerald-900 bg-emerald-50/20 focus:ring-emerald-500/20 focus:border-emerald-500"
                        : "border-slate-200 focus:ring-brand-600/15 focus:border-brand-600 hover:border-slate-300"
                    }`}
                    placeholder="Repeat password"
                  />
                  {passwordsMatch && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-emerald-500 pointer-events-none">
                      <Check size={16} />
                    </div>
                  )}
                </div>
                {passwordsMismatch && (
                  <p className="text-xs font-semibold text-red-600 flex items-center gap-1 mt-1 pl-1">
                    <AlertCircle size={13} className="shrink-0" />
                    Passwords do not match
                  </p>
                )}
                {fieldErrors.confirmPassword && !passwordsMismatch && (
                  <p className="text-xs font-semibold text-red-600 flex items-center gap-1 mt-1 pl-1">
                    <AlertCircle size={13} className="shrink-0" />
                    {fieldErrors.confirmPassword}
                  </p>
                )}
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting || !otp || !newPassword || !confirmPassword}
                  className={`w-full flex justify-center items-center gap-2 py-3.5 px-4 rounded-xl shadow-sm text-sm font-bold text-white transition-all bg-brand-600 hover:bg-brand-700 active:scale-[0.98] ${
                    (isSubmitting || !otp || !newPassword || !confirmPassword) 
                      ? "opacity-60 cursor-not-allowed" 
                      : "hover:shadow-lg hover:shadow-brand-600/25"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Resetting Password...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </button>
              </div>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => { 
                    setStep(1); 
                    setFieldErrors({}); 
                    closeToast(); 
                  }}
                  className="inline-flex items-center justify-center gap-1.5 text-xs font-bold text-slate-500 hover:text-brand-600 transition-colors"
                >
                  <ArrowLeft size={14} />
                  Change Email Address
                </button>
              </div>
            </motion.form>
          )}

          {/* STEP 3: Success Screen */}
          {step === 3 && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-4 space-y-4"
            >
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600 shadow-lg shadow-emerald-500/10">
                <CheckCircle2 size={36} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">Password Changed!</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                  Aapka naya password successfully save ho chuka hai. Ab aap apne naye password ke sath login kar sakte hain.
                </p>
              </div>
              <div className="pt-2">
                <Link
                  to="/my-account"
                  className="w-full flex justify-center items-center py-3.5 px-4 rounded-xl shadow-md shadow-brand-600/20 text-sm font-bold text-white bg-brand-600 hover:bg-brand-700 transition-all hover:shadow-lg"
                >
                  Go to Login
                </Link>
              </div>
            </motion.div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
